import { CommonModule } from '@angular/common';
import { Component, computed, Signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ContentScoreData, ContentScoringItem } from 'listings/interfaces/content-scoring';
import { ScrollToField } from 'listings/interfaces/listingsUtils.interface';
import { ContentScoringService } from 'listings/services/content-scoring.service';
import { ListingsScrollUtilsService } from 'listings/services/listingsScrollUtils.service';
import { BaseModal } from 'shared/abstract/base-modal';
import { ProgressBarComponent } from 'shared/components/progress-bar/progress-bar.component';
import { ID_ZERO } from 'shared/constants/general-constants';

interface ViewItem extends Pick<ContentScoringItem, 'label' | 'featureGroupFieldName' | 'featureGroupId' | 'featureDetailId'> {
    isComplete: boolean;
    note?: string;
    completedCount?: number;
    totalCount?: number;
    subItems?: ContentScoreData[];
}

@Component({
    selector: 'app-content-scoring-modal',
    templateUrl: './content-scoring-modal.component.html',
    styleUrls: ['./content-scoring-modal.component.scss'],
    imports: [ProgressBarComponent, TranslateModule, CommonModule],
})
export class ContentScoringModalComponent extends BaseModal {
    data!: any;

    readonly progressText = computed(() => {
        const value = this.contentScoringService.scoreResult()?.totalScore || 0;

        switch (true) {
            case value >= 100:
                return 'listing.contentScoring.progressPerfect';
            case value >= 66:
                return 'listing.contentScoring.progressAlmostThere';
            case value >= 33:
                return 'listing.contentScoring.progressWorkToDo';
            case value >= 0:
                return 'listing.contentScoring.progressStartAdding';
            default:
                return '';
        }
    });

    readonly items: Signal<ViewItem[]> = computed(() => {
        const scoreResult = this.contentScoringService.scoreResult();
        if (!scoreResult || scoreResult.contentScoreItems?.length === 0) return [];

        return scoreResult.contentScoreItems.map((scoreItem: ContentScoringItem) => {
            const data = scoreItem.data;

            if ('item' in data) {
                const single: ContentScoreData = data.item;
                return {
                    label: scoreItem.label,
                    isComplete: single.isCompleted,
                    note: single.isCompleted
                        ? 'listing.contentScoring.informationProvided'
                        : 'listing.contentScoring.informationNotAllProvided',
                    featureGroupId: scoreItem.featureGroupId,
                    featureDetailId: scoreItem.featureDetailId,
                    featureGroupFieldName: scoreItem.featureGroupFieldName,
                };
            } else if ('items' in data) {
                const multiple: ContentScoreData[] = data.items;
                const completedCount = multiple.filter((d) => d.isCompleted).length;
                const allSubItemsCompleted = completedCount === multiple.length;
                const showIsCompleteIcon =
                    (allSubItemsCompleted && multiple.length > 0) || (allSubItemsCompleted && multiple.length === 0 && !data.enforceExist);

                return {
                    label: scoreItem.label,
                    isComplete: showIsCompleteIcon,
                    completedCount: completedCount,
                    totalCount: multiple.length,
                    note:
                        multiple.length === 0
                            ? this.getNoteBasedOnFeaturedGroupName(scoreItem.featureGroupFieldName)
                            : allSubItemsCompleted
                              ? 'listing.contentScoring.informationAllProvided'
                              : completedCount > 0
                                ? 'listing.contentScoring.informationPartiallyProvided'
                                : 'listing.contentScoring.informationNotAllProvided',
                    subItems: multiple.map((dataItem: ContentScoreData) => ({
                        itemName: dataItem.itemName,
                        itemIdentifier: dataItem.itemIdentifier,
                        isCompleted: dataItem.isCompleted,
                    })),
                    enforceExist: data.enforceExist,
                    featureGroupFieldName: scoreItem.featureGroupFieldName,
                    featureGroupId: scoreItem.featureGroupId,
                    featureDetailId: scoreItem.featureDetailId,
                };
            }

            // Fallback -- should never reach it if backend data is correct
            return {
                label: scoreItem.label,
                isComplete: false,
                note: 'listing.contentScoring.informationNotAllProvided',
                featureGroupId: ID_ZERO,
                featureDetailId: null,
            };
        });
    });

    constructor(
        private listingsScrollUtilsService: ListingsScrollUtilsService,
        public contentScoringService: ContentScoringService,
    ) {
        super();
    }

    navigateAndScrollToField(item: ViewItem, subItem?: ContentScoreData): void {
        if (!item.featureGroupId || item.featureGroupId === ID_ZERO) return;

        const scrollToField: ScrollToField = {
            featureDetailId: item.featureDetailId,
            featureGroupId: item.featureGroupId,
            itemIdentifier: subItem?.itemIdentifier,
            featureGroupFieldName: item.featureGroupFieldName,
            highlight: subItem ? !subItem.isCompleted : !item.isComplete,
        };

        this.listingsScrollUtilsService.fieldToScrollTo.set(scrollToField);
        this.closeModalInstance();
    }

    getNoteBasedOnFeaturedGroupName(featureGroupFieldName: ContentScoringItem['featureGroupFieldName']): string {
        const customFeatureGroupFieldNameNotes = ['roomType', 'outlet'];

        if (featureGroupFieldName && customFeatureGroupFieldNameNotes.includes(featureGroupFieldName)) {
            return `listing.contentScoring.subItemNotes.${featureGroupFieldName}`;
        } else {
            return 'listing.contentScoring.subItemNotes.default';
        }
    }
}
